<template>
    <div id="app" :class="language">
        <img
            src="/static/assets/favicon_100.png"
            style="
                visibility: hidden;
                position: absolute;
                top: 0;
                left: 0;
                width: 0;
                height: 0;
                border: 0;
            "
        />
        <div>
            <div class="browser-warning notices is-top" v-if="showNotSupported">
                <div class="toast adriel-warning is-top">
                    <div>
                        아드리엘은 크롬 브라우저에서 가장 잘 작동됩니다. 현재
                        화면이 잘 보이지 않거나, 최적화된 환경에서 서비스를
                        이용하시려면 크롬 브라우저를 사용해주세요.
                    </div>
                    <img
                        src="/static/assets/img/icons/toast_close_btn.png"
                        @click="showNotSupported = false"
                    />
                </div>
            </div>
            <loading-adriel v-if="isLoadingAdriel" :active="isLoadingAdriel" />
            <SubscriptionWarning v-if="_subscriptionWarningVisible" />
            <SubscriptionPlans v-if="isSubscriptionModalActive" />
            <top-toast />
            <logout v-if="isLogoutModalActive" />
            <ToTop
                nativeOnClick="{this.toTop}"
                v-if="$route.meta.toTopVisible !== false && !isMobileSize"
            />
            <new-campaign
                :active="isNewCampaignModalActive"
                @update:active="setIsNewCampaignModalActive"
                v-if="isPlanA"
            />
            <demo-FAB v-if="isDemo" @confirm="clickSignUpFromDemo" />
            <modal-box
                :active="isDemoAccountModalActive"
                :header-text="$t('DEMO.header_modal')"
                :cancelText="$t('COMMON.cancel')"
                :confirm-text="$t('DEMO.want_to_signup')"
                v-if="isDemo"
                @confirm="clickSignUpFromDemo"
                @cancel="setDemoAccountModalActive(false)"
            >
                <p class="text-center">{{ $t('DEMO.need_real_account') }}</p>
            </modal-box>
            <div id="main-route-view" class="page-wrapper">
                <Navagation v-if="gnbVisible" />
                <router-view />
            </div>
            <currency-modal v-if="currencyModalVisible" />
            <!-- isMfaCheckReady here too: the check can start on another
                 page and resolve after the redirect to the workspace form
                 (TASK-14318); the nudge then waits for the dashboard. -->
            <mfa-nudge-modal
                v-if="mfaCheck.showNudge.value && isMfaCheckReady"
                :on-setup="goToMfaSetup"
                :on-later="mfaCheck.close"
                :on-dismiss="mfaCheck.dismiss"
            />
            <mfa-backup-alert-modal
                v-if="mfaCheck.showBackupAlert.value && isMfaCheckReady"
                :on-regenerate="goToMfaRegenerate"
                :on-later="mfaCheck.close"
            />
            <mfa-relogin-modal
                v-if="mfaReloginQuery"
                :on-finish="finishMfaRelogin"
            />
            <notification />
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations, mapState } from 'vuex';
import ModalBox from '@/components/Common/ModalBox';
import ToTop from '@/components/Dashboard/common/ToTop';
import { numeral } from '@util';
import TopToast from '@/components/Common/TopToast';
import Navagation from '@/components/Navigation/Navigation';

import {
    isValidBrowser,
    deleteElement,
    isValueExist,
    clearLocalStorage,
    getParameterByName,
    getLocalStorage,
    setLocalStorage,
    testerFromArray,
    getAdrielJwt,
    setAdrielJwt,
    getUserFromLocalStorage,
    setAdrielTokens,
} from '@util';

import { CUSTOM_LOGO_WHITELABEL_HOSTS } from '@consts';
import { fromEvent, timer, merge, combineLatest } from 'rxjs';
import {
    distinctUntilChanged,
    startWith,
    map,
    auditTime,
    filter,
    first,
    tap,
    debounceTime,
    partition,
    switchMap,
    skip,
    takeUntil,
    delay,
    share,
    take,
} from 'rxjs/operators';

import {
    UtilsService,
    AuthService,
    UserService,
} from '@/services';
import {
    initMixpanel,
    identifyUser,
    registerSuperProperties,
} from '@/utils/tracker/mixpanel';

import authMixin from '@/mixins/auth';

import { loadLanguageAsync } from './i18n';
import { IFRAME_EVENT_MESSAGE } from '@consts';
import { getAdrielJwtRefreshToken, removeAdrielTokens } from '@util';
import {
    LAST_ACCOUNT_ID_STORAGE_KEY,
    clearStorageOnLogout,
    genLastTabKey,
    genLegacyLastAccountIdKey,
    genLegacyLastTabKey,
    migrateLocalStorageKey,
} from '@/utils/logoutStorage';
import EventBus from '@/plugins/eventBus';
import { useMfaPostLoginCheck } from '@/composable/useMfaPostLoginCheck';
import { MFA } from '@consts';

const MFA_INVALIDATION_REASONS = new Set(['mfa_enabled', 'mfa_disabled']);

/**
 * `POST /auth/refreshJWT` answers 400 (`BAD_JWT_TOKEN_SENT`) when it will not
 * accept the refresh token — malformed, or issued before the user's
 * auth_version changed. 401 is treated the same. Anything without a response,
 * or a 5xx, says nothing about the token.
 */
const isRefreshRejected = (error) =>
    [400, 401].includes(error?.response?.status);

/**
 * Screens a signed-in user sees before they are inside a workspace. The
 * two-factor nudge and the backup-code alert wait until the dashboard: on top
 * of the workspace form they ask for a second decision at once, and their
 * buttons lead to Settings, which needs a workspace (TASK-14318).
 */
const MFA_CHECK_EXCLUDED_ROUTES = new Set(['CreateWorkspace', 'EmptyWs']);

const SubscriptionPlans = () =>
    import('@/components/Payments/SubscriptionPlans.js');
const CurrencyModal = () => import('@/components/Settings/CurrencyModal');
const LoadingAdriel = () => import('@/components/Common/LoadingAdriel');
const NewCampaign = () => import('@/components/Common/NewCampaign');
const Logout = () => import('@/components/Common/Logout.vue');
const DemoFAB = () => import('@/components/Common/DemoFAB');
const SubscriptionWarning = () =>
    import('@/components/Common/SubscriptionWarning');
const Notification = () => import('@/components/Dashboard/Notification');
const MfaNudgeModal = () => import('@/components/Mfa/MfaNudgeModal');
const MfaBackupAlertModal = () =>
    import('@/components/Mfa/MfaBackupAlertModal');
const MfaReloginModal = () => import('@/components/Mfa/MfaReloginModal');

export default {
    name: 'App',
    mixins: [authMixin],
    components: {
        Logout,
        LoadingAdriel,
        ModalBox,
        DemoFAB,
        NewCampaign,
        SubscriptionWarning,
        ToTop,
        SubscriptionPlans,
        TopToast,
        CurrencyModal,
        Notification,
        Navagation,
        MfaNudgeModal,
        MfaBackupAlertModal,
        MfaReloginModal,
    },
    setup() {
        const mfaCheck = useMfaPostLoginCheck();
        return { mfaCheck };
    },
    data() {
        return {
            showNotSupported: false,
            // Login query to sign out to once the two-factor re-login notice
            // ends; non-null while the notice is up (TASK-14321).
            mfaReloginQuery: null,
            postClickAnywhereMessage: () => {
                this.postIframeMessage({
                    type: `CLICK_ANYWHERE`,
                    origin: `VUE`,
                });
            },
            handleMfaSessionInvalidated: (event) => {
                const { type, origin, data } = event.data || {};

                if (
                    type !== IFRAME_EVENT_MESSAGE.MFA_SESSION_INVALIDATED ||
                    origin !== 'REACT'
                )
                    return;

                const reason = data?.reason;
                if (!MFA_INVALIDATION_REASONS.has(reason)) return;

                if (!this.$route.meta.requireLogin) return;

                // No toast: React's re-login notice already told the user (WP14).
                this.forceLogoutToLogin({ reason: 'mfa' });
            },
            handleRefreshTokenRequest: async (event) => {
                const { type, origin } = event.data;

                if (
                    type !== IFRAME_EVENT_MESSAGE.REFRESH_ACCESS_TOKEN ||
                    origin !== 'REACT'
                )
                    return;

                if (process.env.NODE_ENV === `development`) {
                    console.log(`vue - listener`, event.data);
                }

                if (!this.$route.meta.requireLogin) {
                    console.log('No need to refresh token');
                    event.source.postMessage(
                        {
                            type: IFRAME_EVENT_MESSAGE.REFRESH_ACCESS_TOKEN_ERROR,
                            data: 'No need to refresh token',
                        },
                        event.origin
                    );
                    return;
                }

                try {
                    // TODO: handle concurrency
                    if (!AuthService.isRefreshTokenExpired()) {
                        const refreshToken = getAdrielJwtRefreshToken();

                        const tokens = await AuthService.refreshAccessToken({
                            refreshToken,
                        });

                        if (!isValueExist(tokens) || !tokens.token) {
                            throw new Error('Token not refreshed');
                        }

                        setAdrielTokens({
                            ...tokens,
                        });
                        AuthService.setAccessToken$.next(tokens.token);
                        event.source.postMessage(
                            {
                                type: IFRAME_EVENT_MESSAGE.REFRESH_ACCESS_TOKEN_RESPONSE,
                                data: tokens.token,
                            },
                            event.origin
                        );
                        return;
                    }

                    await this.logout();
                    this.$router.push({
                        name: 'Sign/Login',
                        query: R.pick(['planType'], this.$route.query),
                    });
                    throw new Error('Refresh token is expired, logging out');
                } catch (error) {
                    event.source.postMessage(
                        {
                            type: IFRAME_EVENT_MESSAGE.REFRESH_ACCESS_TOKEN_ERROR,
                            data: error.message,
                        },
                        event.origin
                    );

                    // The server refused the refresh token itself: the user's
                    // auth_version moved on (two-factor reset by support, or
                    // turned on/off in another session), so no request of
                    // this session can succeed again. Before this, only the
                    // React page got the error and the user stayed on a
                    // screen where every action failed (TASK-14321). A
                    // network error or a 5xx has no such meaning and keeps
                    // the session.
                    if (isRefreshRejected(error)) {
                        this.showMfaRelogin(
                            R.pick(['planType'], this.$route.query)
                        );
                    }
                }
            },
        };
    },
    beforeDestroy() {
        this.removeClickAnywhereListener();
        this.removeTokenRequestListener();
        this.removeMfaSessionListener();
        this.removeAuthFailureListener();
    },
    methods: {
        ...mapMutations('view', [
            'setWindowDimension',
            'setIsNewCampaignModalActive',
            'setVisibleTo',
        ]),
        ...mapMutations('user', ['setAccountId', 'setUserAccountId', 'login']),
        ...mapActions('iframe', ['postIframeMessage']),
        ...mapActions('user', [
            'logout',
            'fetchWorkspace',
            'fetchMembers',
            'getSubscription',
            'checkSession',
        ]),
        ...mapActions('asset', ['fetchGaEmails']),
        ...mapActions('newDashboard', ['getTableAdAccountsV2']),
        addClickAnywhereListener() {
            window.addEventListener(`click`, this.postClickAnywhereMessage);
        },
        removeClickAnywhereListener() {
            window.addEventListener(`click`, this.postClickAnywhereMessage);
        },

        addTokenRequestListener() {
            window.addEventListener('message', this.handleRefreshTokenRequest);
        },
        removeTokenRequestListener() {
            window.removeEventListener(
                'message',
                this.handleRefreshTokenRequest
            );
        },
        addMfaSessionListener() {
            window.addEventListener(
                'message',
                this.handleMfaSessionInvalidated
            );
        },
        removeMfaSessionListener() {
            window.removeEventListener(
                'message',
                this.handleMfaSessionInvalidated
            );
        },
        checkBrowser() {
            this.showNotSupported = !isValidBrowser();
        },
        clickSignUpFromDemo() {
            this.setDemoAccountModalActive(false);
            this.logout();
            this.$router.push({ name: 'Sign/Join' });
        },
        registerNumeralLocale() {
            if (numeral.locales['ko'] === undefined) {
                numeral.register('locale', 'ko', {
                    delimiters: {
                        thousands: ',',
                        decimal: '.',
                    },
                    abbreviations: {
                        thousand: '천',
                        million: '백만',
                        billion: '십억',
                        trillion: '조',
                    },
                    ordinal: function (number) {
                        return number === 1 ? 'er' : 'ème';
                    },
                    currency: {
                        symbol: '$',
                    },
                });
            }
            if (numeral.locales['ja'] === undefined) {
                numeral.register('locale', 'ja', {
                    delimiters: {
                        thousands: ',',
                        decimal: '.',
                    },
                    abbreviations: {
                        thousand: '千',
                        million: '百万',
                        billion: '十億',
                        trillion: '兆',
                    },
                    currency: {
                        symbol: '¥',
                    },
                });
            }
        },
        replaceQueryString(url, param, value) {
            const re = new RegExp('([?|&])' + param + '=.*?(&|$)', 'i');
            if (url.match(re))
                return url.replace(re, '$1' + param + '=' + value + '$2');
            else return url + '&' + param + '=' + value;
        },
        observeUserAccountId$() {
            const { generateWatchNew$: gen$ } = this;

            const id$ = gen$('$route.query.userAccountId').pipe(
                map(Number),
                filter(R.identity),
                distinctUntilChanged()
            );

            this.$subscribeTo(id$, this.setUserAccountId);
        },
        fetchData$() {
            const { generateWatchNew$: gen$, accountFetched$ } = this;

            const merged$ = merge(
                gen$('cAccountId'),
                gen$('userAccountId')
            ).pipe(
                map(Number),
                filter(R.identity),
                debounceTime(200),
                distinctUntilChanged()
            );

            const [login$, logout$] = gen$('user').pipe(
                map(R.defaultTo({})),
                partition(R.propSatisfies(isValueExist, 'id'))
            );

            const obs$ = login$.pipe(
                distinctUntilChanged(R.eqProps('id')),
                switchMap(() => {
                    return accountFetched$.pipe(
                        map(Number),
                        filter(R.identity),
                        distinctUntilChanged(),
                        switchMap(() => merged$),
                        takeUntil(logout$),
                        share()
                    );
                })
            );

            this.$subscribeTo(obs$, () => {
                this.getSubscription();
                this.getTableAdAccountsV2();

                this.fetchGaEmails();
            });

            // const changed$ = obs$.pipe(skip(1));

            // this.$subscribeTo(changed$, () => {
            //     window.location.reload();
            // });
        },
        observePlanTypeChange$() {
            const pType$ = this.generateWatchNew$('planType').pipe(
                filter(R.identity),
                distinctUntilChanged()
            );

            // fetch workspace again
            const tChange$ = pType$.pipe(skip(1));
            this.$subscribeTo(tChange$, this.fetchWorkspace);
        },
        observePlanType$() {
            const t$ = this.generateWatchNew$('planType').pipe(
                filter(R.identity),
                distinctUntilChanged()
            );

            const r$ = this.generateWatchNew$('$route', { deep: true }).pipe(
                map(R.defaultTo({})),
                filter(({ name }) => name),
                distinctUntilChanged(R.equals)
            );

            this.$subscribeTo(combineLatest(t$, r$), ([type]) => {
                const oldQuery = this.$route.query;

                if (oldQuery.planType === type) {
                    return;
                }
                const query = R.assoc('planType', type, oldQuery);

                if (!R.equals(oldQuery, query)) {
                    this.$router.replace({
                        query,
                    });
                }
            });
        },
        handleLastTab$() {
            const { generateWatchNew$: gen$ } = this;
            // will combine userId, accountId, tab

            const _distinctUntilChanged = distinctUntilChanged(R.equals);
            const identityFilter = filter(R.identity);

            const userId$ = gen$('user.id').pipe(identityFilter);

            const accountId$ = gen$('account.id').pipe(identityFilter);

            const redirected$ = gen$('$route.query.redirected');

            const ids$ = combineLatest(userId$, accountId$);

            // retreive
            const fromLocal$ = redirected$.pipe(
                filter((v) => v == 1),
                switchMap(() => ids$)
            );

            this.$subscribeTo(fromLocal$, ([userId, accountId]) => {
                // The ids can arrive after the user has already moved on
                // (e.g. the MFA nudge's Settings link); restoring the last
                // tab then would undo that navigation.
                if (this.$route.query.redirected != 1) return;

                // remove from query
                this.queryStringThenRemove('redirected');

                try {
                    const key = genLastTabKey(accountId);
                    migrateLocalStorageKey(
                        genLegacyLastTabKey(userId, accountId),
                        key
                    );
                    let value = getLocalStorage(key);
                    value = JSON.parse(value) || {};

                    const { tab } = value;

                    const banned = testerFromArray(
                        [
                            'material',
                            'notfound',
                            'DashboardSharedView',
                            'sign',
                            'New/Proposal',
                            'MetricSchema',
                        ],
                        tab
                    );

                    if (banned || tab === this.$route.name) {
                        return;
                    }

                    const hasNoPlayDemo = getLocalStorage('hasNoPlayDemo');
                    if (!hasNoPlayDemo && !this.isMobileSize) {
                        this.$router.push({ name: tab });
                    }
                } catch (err) {
                    console.error(err);
                }
            });

            // saving part
            const tab$ = gen$('$route.name').pipe(identityFilter);

            const [user$, noUser$] = gen$('globalUser').pipe(
                map(R.defaultTo({})),
                partition(R.propSatisfies(isValueExist, 'id'))
            );

            const combined$ = user$.pipe(
                switchMap(() => {
                    return combineLatest(
                        ids$,
                        tab$,

                        ([userId, accountId], tab) => {
                            return {
                                userId,
                                accountId,
                                tab,
                            };
                        }
                    ).pipe(
                        _distinctUntilChanged,
                        debounceTime(1000),
                        takeUntil(noUser$)
                    );
                })
            );

            this.$subscribeTo(combined$, ({ accountId, tab }) => {
                const key = genLastTabKey(accountId);

                setLocalStorage(key, JSON.stringify({ tab }));
            });

            // Also remember the workspace on the server (users.meta), so it
            // follows the user to other devices and survives the logout wipe.
            // The device copy (lastAccountId) is still written in setAccountId
            // and stays the fallback while the server has no value yet.
            // Deduplicated outside the switchMap: the user object is replaced
            // on every /auth/user, which restarts the inner watch (it emits
            // immediately) and would otherwise resend the same workspace.
            const lastWorkspace$ = user$.pipe(
                switchMap(() =>
                    ids$.pipe(
                        map(([, accountId]) => accountId),
                        takeUntil(noUser$)
                    )
                ),
                distinctUntilChanged(),
                debounceTime(1000)
            );

            this.$subscribeTo(lastWorkspace$, (accountId) => {
                // A backoffice admin looking into a customer's workspace must
                // not have it remembered as their own.
                if (this.isUsingUserAccount) return;

                UserService.patchUserMeta({ lastWorkspaceId: accountId }).catch(
                    () => {
                        // Best effort: an older server has no /user/meta yet.
                    }
                );
            });
        },
        observeQueryAccountId$() {
            const { accountFetched$, generateWatchNew$: gen$ } = this;

            const obs$ = accountFetched$.pipe(
                filter(R.identity),
                delay(1000),
                switchMap(() => {
                    return gen$('$route.query.accountId').pipe(
                        map(Number),
                        filter(R.identity),
                        distinctUntilChanged(),
                        skip(1)
                    );
                })
            );

            this.$subscribeTo(obs$, () => window.location.reload());
        },

        watchURLForGTag$() {
            const { generateWatchNew$: gen$ } = this;

            const routeObs$ = gen$('$route').pipe(
                map(R.pick(['path', 'name'])),
                distinctUntilChanged(R.equals)
            );

            this.$subscribeTo(routeObs$, (router) => {
                const { path, name } = router;

                if (path !== '/') {
                    const isDashboard = path.indexOf('dashboard') > -1;
                    const isProposal = path.indexOf('proposal') > -1;

                    let page_path = isDashboard ? '/dashboard' : path;
                    page_path = isProposal ? '/proposal' : path;

                    this.$gtag.event('page_view', {
                        page_title: name,
                        page_path,
                    });

                    // hubspot
                    var _hsq = (window._hsq = window._hsq || []);
                    _hsq.push(['setPath', `/#${path}`]);
                    _hsq.push(['trackPageView']);
                }

                /**
                 * Empty window dataLayer when router updates
                 */
                // for (const item of window.dataLayer) {
                //     if (
                //         item.event &&
                //         item.event !== 'gtm.dom' &&
                //         item.event !== 'sign_up'
                //     ) {
                //         window.dataLayer.pop();
                //     }

                //     if (item.length !== 0 && item[0] === 'event') {
                //         window.dataLayer.pop();
                //     }
                // }
            });
        },

        afterHubSpotLoaded$() {
            const { generateWatchNew$: gen$, isDemo, isWhitelabelUrl } = this;

            const user$ = gen$('user').pipe(distinctUntilChanged());

            const route$ = gen$('$route.path').pipe(
                distinctUntilChanged(),
                debounceTime(150),
                switchMap(() => user$)
            );

            this.$subscribeTo(route$, (user) => {
                if (isDemo || !user) {
                    this.hideChatbot();
                    return;
                }

                const shouldHideChatbotForWhitelabel =
                    isWhitelabelUrl &&
                    CUSTOM_LOGO_WHITELABEL_HOSTS[window.location.hostname]
                        ?.hideChatbot;

                if (shouldHideChatbotForWhitelabel) {
                    this.hideChatbot();
                    return;
                }

                if (this.$route.meta.hideChatbot) {
                    this.hideChatbot();
                    return;
                }

                this.loadChatbot();
            });
        },
        checkJwt() {
            const { generateWatchNew$: gen$ } = this;

            const fetchUser$ = gen$('$route.meta').pipe(
                filter(isValueExist),
                map(R.prop('fetchUser')),
                map((v) => v === false),
                map(R.not),
                filter(R.identity),
                distinctUntilChanged(),
                first()
            );

            this.$subscribeTo(fetchUser$, () => {
                const token = getAdrielJwt();

                if (token) {
                    setAdrielJwt(token);
                    AuthService.setAccessToken$.next(token);

                    const user = getUserFromLocalStorage();

                    if (user) {
                        try {
                            migrateLocalStorageKey(
                                genLegacyLastAccountIdKey(user.id),
                                LAST_ACCOUNT_ID_STORAGE_KEY
                            );

                            const candidates = [
                                getParameterByName('userAccountId'),
                                getParameterByName('accountId'),
                                getLocalStorage(LAST_ACCOUNT_ID_STORAGE_KEY),
                            ].filter(R.identity);

                            const wsId = candidates[0] || 0;

                            axios.defaults.headers.common.AccountId = wsId;

                            this.fetchWorkspaceById(wsId);
                        } catch (e) {
                            console.log(e);
                        }
                    }

                    this.checkSession();
                }
            });
        },
        refreshJwt() {
            const { generateWatchNew$: gen$ } = this;
            const requireLogin$ = gen$('$route.meta.requireLogin').pipe(
                debounceTime(500),
                map(Boolean),
                filter(R.identity),
                take(1)
            );

            this.$subscribeTo(requireLogin$, () => {
                console.log('refreshJwt start because requireLogin');
                const refreshToken = getAdrielJwtRefreshToken();

                if (!refreshToken) return;

                AuthService.refreshAccessToken({
                    refreshToken,
                })
                    .then((tokens) => {
                        if (!isValueExist(tokens) || !tokens.token) {
                            throw new Error('Token not refreshed');
                        }
                        setAdrielTokens({ ...tokens });
                        AuthService.setAccessToken$.next(tokens.token);
                    })
                    .catch((e) => {
                        console.error(e);
                        this.logout();
                        this.$router.push({
                            name: 'Sign/Login',
                            query: R.pick(['planType'], this.$route.query),
                        });
                    });
            });
        },

        watchUserLogin$() {
            const { generateWatchNew$: gen$ } = this;

            const obs$ = gen$('$route.meta.requireLogin').pipe(
                debounceTime(500),
                distinctUntilChanged(),
                filter(R.equals(true)),
                switchMap(() =>
                    gen$('user').pipe(
                        tap(() => {
                            this.checkPlayedMobileDemoPage();
                        }),
                        debounceTime(1000),
                        map(isValueExist),
                        filter(R.equals(false)),
                        distinctUntilChanged(),
                        takeUntil(
                            gen$('$route.meta.requireLogin', {
                                immediate: false,
                            }).pipe(map(Boolean), filter(R.equals(false)))
                        )
                    )
                )
            );

            this.$subscribeTo(obs$, () => {
                window.location.href = '/';
            });
        },

        fetchWsByUserId$() {
            const { generateWatchNew$: gen$ } = this;
            const id$ = gen$('user.id').pipe(
                filter(R.identity),
                distinctUntilChanged()
            );

            this.$subscribeTo(id$, (id) => {
                if (this.$gtag) {
                    this.$gtag.config({
                        user_id: id,
                    });
                }

                // Mixpanel: identify BEFORE init so internal @adriel.* emails
                // are detected before Mixpanel initializes (prevents event leaks).
                identifyUser({ userId: id, email: this.user.email });
                initMixpanel();
                registerSuperProperties({
                    workspace_id: String(this.accountId),
                    account_type:
                        this.account &&
                        this.account.meta &&
                        this.account.meta.isAgency
                            ? 'agency'
                            : 'brand',
                    language: this.user.language,
                });

                this.fetchWorkspaceList(id);
            });
        },
        checkPlayedMobileDemoPage() {
            const { isMobileSize } = this;
            const hasNoPlayDemo = getLocalStorage('hasNoPlayDemo');
            isMobileSize &&
                hasNoPlayDemo &&
                this.$router.push({ name: 'MobileDemo' });
        },
        handleCurrencyModal$() {
            console.log('handleCurrency');
            const { generateWatchNew$: gen$ } = this;

            // WS should be fetched
            const accountId$ = gen$('globalAccount.id').pipe(
                filter(R.identity)
            );

            // check current route can show currency modal
            const canShowCurrency$ = gen$('$route.meta.showCurrencyModal').pipe(
                debounceTime(200),
                map(R.defaultTo(false)),
                filter(R.equals(true))
            );

            const obs$ = accountId$.pipe(
                switchMap(() => canShowCurrency$),
                delay(500),
                first()
            );

            this.$subscribeTo(obs$, () => {
                // reportMode: block
                const isReportMode = R.compose(
                    isValueExist,
                    R.path(['$route', 'query', 'isReportMode'])
                )(this);

                // check currency modal
                // account.is_currency_editable
                // if (
                //     this.isPlanT &&
                //     R.pathEq(['account', 'is_currency_editable'], true, this) &&
                //     !this.isUsingUserAccount &&
                //     !isReportMode
                // ) {
                //     this.setVisibleTo({
                //         key: 'currencyModalVisible',
                //         value: true,
                //     });
                // }
            });
        },
        goToMfaSetup() {
            this.mfaCheck.close();
            this.$router.push({
                name: 'Settings',
                params: { tab: 'account' },
                query: { mfa: MFA.SETTINGS_DEEPLINK.SETUP },
            });
        },
        goToMfaRegenerate() {
            this.mfaCheck.close();
            this.$router.push({
                name: 'Settings',
                params: { tab: 'account' },
                query: { mfa: MFA.SETTINGS_DEEPLINK.REGENERATE },
            });
        },
        /**
         * Signs the user out of a session the server no longer accepts and
         * lands on the login page. Shared by the three forced exits: the
         * two-factor hand-off from React, a refresh the server rejected, and
         * `auth-failed` from our own requests.
         */
        forceLogoutToLogin(query = {}) {
            // Several requests can fail at once, and the logout request itself
            // may fail the same way. The flag is never cleared: the flow ends
            // in a reload.
            if (this.isForcingLogout) return;
            this.isForcingLogout = true;
            // Unsaved Edit Mode changes can't be saved without a session, so
            // the dashboard's leave guards must not stop or question this exit
            // (no leave modal, no browser "Leave site?" prompt on the reload).
            // React's own beforeunload guard, inside the iframe, is told the
            // same: the reload below can start while the dashboard is still
            // mounted, because a redirect in a route guard (the accountId
            // beforeEach) counts as an abort here and `reload` runs on abort.
            this.$store.commit('view/setIsForcingLogout', true);
            this.postIframeMessage({
                type: IFRAME_EVENT_MESSAGE.SESSION_ENDED,
                origin: 'VUE',
            });

            // Ending the server session is what makes this a logout.
            // Clearing only our own tokens is not enough: the server
            // caches the user for 10s when validating passport sessions
            // (`memoizedQueryUser`), so for those 10s the session still
            // authenticates, `checkSession` in the router guard succeeds,
            // and the guard sends the user straight back to MyCampaigns.
            // Disabling two-factor always lands inside that window, so it
            // never logged the user out at all (WP11, 2026-09-21).
            this.logout()
                .catch(() => {
                    // The session may already be gone — drop our own state
                    // anyway so we never leave stale tokens behind.
                    removeAdrielTokens();
                    clearStorageOnLogout();
                    this.$store.commit('user/clearUser');
                    this.$store.commit('newDashboard/setViews', []);
                    this.$store.commit('newDashboard/setOriginViews', {});
                })
                .finally(() => {
                    // Reload once the login page is up, like `logoutUser`
                    // does. Without it the next sign-in of the same user
                    // in this tab never loads the workspace: the App-level
                    // streams (`fetchWsByUserId$`, `fetchData$`) and
                    // `fetchWorkspaceById`'s `lastId` treat the same
                    // `user.id` as "already handled", and `clearUser`
                    // swapped the store's `accountId$` for a new Subject
                    // that those subscriptions never see (QA 2026-09-23;
                    // root fix tracked in docs/features/mfa/findings.md).
                    const reload = () => this.$router.go();

                    this.$router.push(
                        { name: 'Sign/Login', query },
                        reload,
                        reload
                    );
                });
        },
        /**
         * The server refused this session's refresh token: its auth_version
         * moved on, which only a two-factor change does (support reset, or
         * turned on/off in another session). Tell the user before signing
         * out, like React does after its own change (Design 10-07). The
         * notice ends in `forceLogoutToLogin`; other failures meanwhile wait
         * for it.
         */
        showMfaRelogin(query = {}) {
            if (this.isForcingLogout || this.mfaReloginQuery) return;
            this.mfaReloginQuery = query;
        },
        finishMfaRelogin() {
            this.forceLogoutToLogin(this.mfaReloginQuery || {});
        },
        handleAuthFailure({ refreshRejected = false } = {}) {
            const requireLogin = this.$route.meta.requireLogin;

            if (!requireLogin || this.mfaReloginQuery) return;

            const query = R.pick(['planType'], this.$route.query);
            if (refreshRejected) {
                this.showMfaRelogin(query);
            } else {
                this.forceLogoutToLogin(query);
            }
        },
        addAuthFailureListener() {
            EventBus.$on('auth-failed', this.handleAuthFailure);
        },
        removeAuthFailureListener() {
            EventBus.$off('auth-failed', this.handleAuthFailures);
        },
    },
    computed: {
        ...mapGetters('user', [
            'user',
            'account',
            'accounts',
            'accountId',
            'defaultAccountId',
            'sharingMembers',
            'subscriptionStatus',
            'isAdmin',
        ]),
        ...mapState('user', [
            'localRegister',
            'userAccountId',
            'subscription',
            'isUsingUserAccount',
        ]),
        ...mapState('view', [
            'isDemoAccountModalActive',
            'isNewCampaignModalActive',
            'isLoadingAdriel',
            'currencyModalVisible',
            'isSubscriptionModalActive',
        ]),
        ...mapGetters('view', [
            'subscriptionWarningVisible',
            'isLogoutModalActive',
        ]),
        gnbVisible: R.pathOr(false, ['$route', 'meta', 'gnbVisible']),
        // A verified user on an in-app page. `meta.requireLogin` alone is not
        // enough: many in-app routes (e.g. Alarms) and the /sign pages leave
        // it unset.
        isMfaCheckReady() {
            const route = this.$route;
            if (!this.user?.email_verified || !route.name) return false;
            if (route.path.startsWith('/sign')) return false;
            if (MFA_CHECK_EXCLUDED_ROUTES.has(route.name)) return false;
            return (
                route.meta.requireLogin !== false &&
                route.meta.fetchUser !== false
            );
        },
        _subscriptionWarningVisible() {
            return (
                this.subscriptionWarningVisible &&
                this.$route.meta.needSubscription
            );
        },
        queryAccountId: R.path(['$route', 'query', 'accountId']),
    },
    created() {
        this.checkBrowser();
        this.registerNumeralLocale();
        this.observeUserAccountId$();
        this.refreshJwt();
        this.checkJwt();
        this.addAuthFailureListener();
    },
    watch: {
        accountId(value = {}) {
            registerSuperProperties({
                workspace_id: String(value),
                account_type:
                    this.account &&
                    this.account.meta &&
                    this.account.meta.isAgency
                        ? 'agency'
                        : 'brand',
            });
        },
        'user.language': {
            handler(value = 'en') {
                if (this.globalUser === null) {
                    return;
                }
                loadLanguageAsync(value);
                document.querySelector('html').setAttribute('lang', value);
            },
            immediate: true,
            deep: true,
        },
        queryAccountId: {
            handler(value) {
                const numValue = Number(value);

                // value is same with accountId
                const invalidRequest =
                    isValueExist(this.account) &&
                    (numValue === this.accountId || !numValue);
                const isInvalid =
                    !isValueExist(this.user) ||
                    !isValueExist(this.accounts) ||
                    invalidRequest ||
                    value === undefined;

                if (isInvalid) return;

                const hasAccountId = R.any(
                    R.propEq('id', numValue),
                    this.accounts
                );
                const _accountId = hasAccountId
                    ? numValue
                    : this.defaultAccountId;

                // if it's same as current do not fetch again
                if (this.accountId != _accountId) {
                    this.setAccountId(_accountId);
                    this.fetchWorkspace();
                }

                this.fetchMembers();
            },
            immediate: true,
        },
        cAccountId: {
            handler(value) {
                if (!Number(value)) {
                    return;
                }

                let router = this.$route;

                // if redirected
                if (R.propEq('path', '/', router)) {
                    router = R.path(['$router', 'history', 'pending'], this);
                }

                this.$router.replace({
                    path: router.path,
                    query: R.assoc('accountId', value, router.query),
                });
            },
            immediate: true,
        },
    },
    mounted() {
        /**
         * @note customer division, remove query param ?ga=...
         */

        if (this.$gtag) {
            this.$gtag.linker({
                domains: ['m.adriel.com'],
            });
        }

        this.fetchData$();

        const size$ = fromEvent(window, 'resize').pipe(
            auditTime(150),
            startWith('init'),
            map(() => ({
                width: window.innerWidth,
                height: window.innerHeight,
            })),
            distinctUntilChanged(R.equals)
        );

        // remove privacy policy after css injected
        this.$subscribeTo(size$, this.setWindowDimension);

        const privacy$ = timer(5000, 2000).pipe(
            map(() => document.querySelector('body>#iub-pp-container')),
            filter(R.identity),
            first()
        );

        this.$subscribeTo(privacy$, deleteElement);

        this.$nextTick(() => {
            const utm$ = this.generateWatchNew$('$route.query', {
                deep: true,
            }).pipe(
                map(
                    R.pick([
                        'utm_source',
                        'utm_medium',
                        'utm_campaign',
                        'utm_content',
                        'utm_term',
                        'referral_code',
                    ])
                ),
                filter(isValueExist)
            );

            this.$subscribeTo(utm$, (utmQueries) => {
                UtilsService.saveUtm(utmQueries).then(() => {
                    console.log('utm saved');
                });
            });
        });

        this.observePlanTypeChange$();

        this.observePlanType$();

        this.observeQueryAccountId$();

        this.handleLastTab$();

        this.watchURLForGTag$();
        this.watchUserLogin$();

        this.fetchWsByUserId$();

        this.addClickAnywhereListener();
        this.addTokenRequestListener();
        this.addMfaSessionListener();

        this.$watch(
            () => this.isMfaCheckReady,
            (val) => {
                if (val) this.mfaCheck.check();
            },
            { immediate: true }
        );
        this.$watch(
            () => this.user,
            (user) => {
                if (!user) this.mfaCheck.reset();
            }
        );

        this.handleCurrencyModal$();

        console.log('hubspot ready', this.user);

        window.addEventListener('productfruits_ready', () => {
            console.log('productfruits ready');
            this.afterHubSpotLoaded$();
        });
    },
    destroyed() {
        clearLocalStorage();
    },
};
</script>
<style lang="scss">
@import '@design/font_face.scss';
@import './styles/main.scss';
@import './styles/components/index.scss';

#main-route-view {
    &.page-wrapper {
        display: flex;
        width: 100vw;
        height: 100vh;
        min-height: 100vh;
        max-height: 100vh;
        background-color: #fff;

        @media screen and (max-width: 1023px) {
            flex-direction: column;
            overflow: hidden;
        }
    }
}

#app {
    background-color: #fff;
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    /* min-height: 100vh; */
    /* min-width: 1280px; */
    @media screen and (max-width: 1000px) {
        min-width: initial;
    }
    & > div:first-child {
        transition: all 0.5s ease;
    }

    .browser-warning {
        & > .toast {
            align-items: flex-start;
            position: relative;
            padding: 12px 30px;
            max-width: 400px;
            word-break: keep-all;
            word-wrap: break-word;

            img {
                margin-top: 2px;
                position: absolute;
                cursor: pointer;
                right: 10px;
                top: 10px;
            }
        }
    }
}

main {
    flex: 1 0 auto;
}

.modal-content {
    padding: 0px !important;
}

.ui-widget-content {
    z-index: 1005;
}
.modal_title {
    padding: 0px !important;
}

/*mobile*/
@media screen and (max-width: 1000px) {
    #app {
        overflow-x: auto;

        /* CSS specific to iOS devices */
        padding-right: constant(safe-area-inset-right);
        padding-left: constant(safe-area-inset-left);
        padding-right: env(safe-area-inset-right);
        padding-left: env(safe-area-inset-left);

        @supports (-webkit-overflow-scrolling: touch) {
            -webkit-overflow-scrolling: touch; // for scrolling in safari in iOS
        }
    }
}
@import './styles/print.scss';
</style>
